import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Lock, X, Check, Sparkles } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Day-7 Hard Lock — non-dismissible Stripe checkout overlay.
 * On "Cancel" attempt: render One-Time Retention Offer (20% off first 3 months).
 */
export default function HardLockModal({ open, allowDismiss = false, onClose }) {
  const [step, setStep] = useState("primary"); // primary | downsell
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("yearly");
  const [region, setRegion] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/region").then(({ data }) => setRegion(data)).catch(() => {});
  }, [open]);

  const sym = region?.symbol || "$";
  const cur = (region?.currency || "usd").toUpperCase();
  const priceMonthly = region?.price_preview?.monthly?.amount ?? 29;
  const priceYearly = region?.price_preview?.yearly?.amount ?? 240;
  const priceDownsell = region?.price_preview?.downsell?.amount ?? 23.2;

  const startCheckout = async (packageId) => {
    try {
      setLoading(true);
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout URL missing");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAttemptClose = () => {
    if (step === "primary") {
      setStep("downsell");
      return;
    }
    if (allowDismiss && onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleAttemptClose(); }}>
      <DialogContent
        data-testid="hard-lock-modal"
        className="max-w-[680px] bg-charcoal border-border p-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => { if (!allowDismiss) e.preventDefault(); }}
      >
        {step === "primary" ? (
          <div className="sr-fade-in">
            <div className="px-7 pt-7 pb-3 border-b border-border" style={{ background: "linear-gradient(180deg, rgba(0,128,128,0.12), transparent)" }}>
              <div className="flex items-center gap-2 mb-2">
                <Lock className="h-4 w-4 text-teal" />
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-teal">Subscription Required</span>
              </div>
              <DialogHeader>
                <DialogTitle className="text-2xl font-display tracking-tight text-white">
                  Activate <span className="font-light">SaiResponse</span> Premium
                </DialogTitle>
                <DialogDescription className="sr-only">Choose a plan to continue using SaiResponse.</DialogDescription>
              </DialogHeader>
              <p className="text-sm text-white/60 mt-2 max-w-[520px]">
                Your 7-day trial is complete. Choose a plan to continue producing leadership-grade communications without a daily limit.
              </p>
            </div>

            <div className="p-7 grid md:grid-cols-2 gap-4">
              {/* Monthly */}
              <button
                data-testid="plan-monthly"
                onClick={() => setSelected("monthly")}
                className={`text-left p-5 rounded-sm border transition-all ${
                  selected === "monthly"
                    ? "border-teal sr-teal-glow bg-teal/[0.04]"
                    : "border-border hover:border-white/30 bg-black/30"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-white/50">Monthly</div>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-3xl font-display font-black text-white">{sym}{Math.round(priceMonthly)}</span>
                      <span className="text-white/50 text-sm">/mo · {cur}</span>
                    </div>
                  </div>
                  {selected === "monthly" && <Check className="h-4 w-4 text-teal" />}
                </div>
                <p className="mt-2 text-xs text-white/50">Cancel anytime · Test mode</p>
              </button>

              {/* Yearly */}
              <button
                data-testid="plan-yearly"
                onClick={() => setSelected("yearly")}
                className={`relative text-left p-5 rounded-sm border transition-all ${
                  selected === "yearly"
                    ? "border-teal sr-teal-glow bg-teal/[0.04]"
                    : "border-border hover:border-white/30 bg-black/30"
                }`}
              >
                <span className="absolute -top-2 right-3 text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 bg-teal text-white rounded-sm">
                  Save 30%
                </span>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-white/50">Yearly</div>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-3xl font-display font-black text-white">{sym}{Math.round(priceYearly)}</span>
                      <span className="text-white/50 text-sm">/yr · {cur}</span>
                    </div>
                    <div className="text-[12px] text-teal/90 mt-1 font-mono">≈ {sym}{Math.round(priceYearly / 12)}/mo</div>
                  </div>
                  {selected === "yearly" && <Check className="h-4 w-4 text-teal" />}
                </div>
                <p className="mt-2 text-xs text-white/50">Billed annually · Best value</p>
              </button>
            </div>

            <ul className="px-7 pb-5 space-y-2 text-sm text-white/70">
              {[
                "Unlimited generations across all 3 personas × 9 modes",
                "Magic Paste & Demo Sample library",
                "Strict privacy: zero retention of input or output text",
                "API key access — ready for Chrome extension integration",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-teal shrink-0" /> {f}
                </li>
              ))}
            </ul>

            <DialogFooter className="px-7 py-5 border-t border-border bg-black/30 flex-row gap-3 justify-between">
              <Button
                data-testid="hard-lock-cancel-btn"
                variant="ghost"
                onClick={() => setStep("downsell")}
                className="text-white/50 hover:text-white"
              >
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button
                data-testid="hard-lock-checkout-btn"
                disabled={loading}
                onClick={() => startCheckout(selected)}
                className="bg-teal hover:bg-teal-hover text-white rounded-sm px-6"
              >
                {loading ? "Redirecting..." : `Continue to checkout — ${selected === "yearly" ? `${sym}${Math.round(priceYearly)}/yr` : `${sym}${Math.round(priceMonthly)}/mo`} ${cur}`}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="sr-fade-in" data-testid="downsell-view">
            <div className="px-7 pt-7 pb-3 border-b border-border" style={{ background: "linear-gradient(180deg, rgba(255,87,34,0.10), transparent)" }}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-orange-alert" />
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{ color: "#FF5722" }}>
                  One-Time Retention Offer
                </span>
              </div>
              <DialogHeader>
                <DialogTitle className="text-2xl font-display tracking-tight text-white">
                  Wait — take 20% off your first 3 months
                </DialogTitle>
                <DialogDescription className="sr-only">One-time retention offer for first 3 months.</DialogDescription>
              </DialogHeader>
              <p className="text-sm text-white/60 mt-2 max-w-[520px]">
                Before you go: lock in <span className="text-white font-semibold">{sym}{priceDownsell.toFixed(2)}/month</span> for the first 3 months. Then standard {sym}{Math.round(priceMonthly)}/mo {cur}. No tricks.
              </p>
            </div>

            <div className="p-7">
              <div className="p-6 rounded-sm border border-orange-alert/40 sr-teal-glow" style={{ background: "rgba(255,87,34,0.05)", boxShadow: "0 0 0 1px rgba(255,87,34,0.4), 0 0 24px -8px rgba(255,87,34,0.45)" }}>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-display font-black text-white">{sym}{priceDownsell.toFixed(2)}</span>
                  <span className="text-white/60">/mo · {cur} · first 3 months</span>
                </div>
                <div className="mt-1 text-sm text-white/50 line-through">{sym}{priceMonthly.toFixed(2)}/mo</div>
                <div className="mt-3 text-xs font-mono uppercase tracking-[0.16em] text-orange-alert">Offered only once</div>
              </div>
            </div>

            <DialogFooter className="px-7 py-5 border-t border-border bg-black/30 flex-row gap-3 justify-between">
              <Button
                data-testid="downsell-decline"
                variant="ghost"
                onClick={() => setStep("primary")}
                className="text-white/50 hover:text-white"
              >
                Go back
              </Button>
              <Button
                data-testid="downsell-accept"
                disabled={loading}
                onClick={() => startCheckout("downsell")}
                className="bg-orange-alert hover:bg-orange-alert/90 text-white rounded-sm px-6"
              >
                {loading ? "Redirecting..." : `Claim 20% off → ${sym}${priceDownsell.toFixed(2)}/mo ${cur}`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
